import styled from 'styled-components'
import Select from 'react-select'
import { Form } from 'semantic-ui-react'

/**
 * FormSelect Styled Components
 *
 * Single Form.Field wrapper that:
 * - Carries Semantic UI's `.field` class so `Form.Group widths='equal'` distributes correctly
 * - Provides position: relative for the absolute-positioned FormFieldError label
 *
 * Uses transient props ($error, $required, $disabled) to keep them off the DOM.
 */
export const StyledFormField = styled(Form.Field)`
  position: relative;
  padding: 0.5em 0;

  label {
    display: block;
    padding: 0.3em 0 0.2em 0.2em;
    font-size: 0.92857143em;
  }

  ${(props) =>
    props.$required &&
    `
    label {
      position: relative;
      padding-left: 0.75em;

      &::before {
        color: var(--red);
        content: '* ';
        font-size: 1.4em;
        left: 0;
        position: absolute;
        top: 0.2em;
      }
    }
  `}

  /* Suppress Semantic UI's auto-asterisk on required labels so we don't
     double up with the custom ::before above. !important is required because
     Semantic UI's '.ui.form .required.field > label::after' selector outranks
     the styled-component class selector on specificity. */
  &.required label::after {
    content: none !important;
  }
`

/**
 * Backwards-compatible alias.
 */
export const StyledFieldContainer = StyledFormField

/**
 * StyledSelect - Styled wrapper for react-select
 * Matches Semantic UI styling using styled-components
 *
 * Uses $ prefix for transient props to prevent DOM warnings
 */
export const StyledSelect = styled(Select)`
  /* Control - main input container */
  .react-select__control {
    min-height: 40px; /* Accounts for value-container padding + borders */
    border: 1px solid
      ${(props) => (props.$error ? '#ca0808' : 'rgba(34,36,38,.15)')};
    border-radius: 0.28571429rem;
    box-shadow: none;
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'default')};

    ${(props) =>
      props.$disabled &&
      `
      background-image: repeating-linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.5),
        rgba(255, 255, 255, 0.5) 5px,
        rgba(243, 243, 243, 0.5) 5px,
        rgba(243, 243, 243, 0.5) 10px
      );
      opacity: 0.9;
    `}

    &:hover {
      border-color: ${(props) =>
        props.$error ? '#ca0808' : 'rgba(34,36,38,.15)'};
    }
  }

  .react-select__control--is-focused {
    border-color: ${(props) =>
      props.$error ? '#ff0000' : 'var(--primary)'} !important;
    box-shadow: none;

    &:hover {
      border-color: ${(props) => (props.$error ? '#ff0000' : 'var(--primary)')};
    }
  }

  /* Value container */
  .react-select__value-container {
    padding: 0.67857143em 1em;
  }

  /* Input */
  .react-select__input-container {
    margin: 0;
    padding: 0;
    line-height: 1.21428571em;
  }

  .react-select__input {
    line-height: 1.21428571em;
  }

  /* Fomantic UI's '.ui.form input' rules cascade onto react-select's hidden
     DummyInput (rendered inside .react-select__value-container for
     non-searchable selects), giving it 38px height + padding + border. That
     stretches the grid row and the whole control. Neutralize it here for any
     input inside the value-container — both the DummyInput and the searchable
     input must remain layout-flat. */
  .react-select__value-container input {
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
    height: auto !important;
    min-height: 0 !important;
    padding: 0 !important;
  }

  /* Placeholder */
  .react-select__placeholder {
    color: var(--textarea-placeholder-color);
    line-height: 1.21428571em;
  }

  /* Single value (selected text) */
  .react-select__single-value {
    color: rgba(0, 0, 0, 0.87);
    line-height: 1.21428571em;
  }

  /* Multi value (tags) */
  .react-select__multi-value {
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 0.28571429rem;
  }

  .react-select__multi-value__label {
    color: rgba(0, 0, 0, 0.87);
    padding: 3px 6px;
  }

  .react-select__multi-value__remove {
    color: rgba(0, 0, 0, 0.87);

    &:hover {
      background-color: rgba(0, 0, 0, 0.1);
      color: rgba(0, 0, 0, 0.87);
    }
  }

  /* Menu (dropdown) */
  .react-select__menu {
    border: 1px solid var(--primary);
    border-radius: 0.28571429rem;
    margin-top: 2px;
    box-shadow: 0 2px 3px 0 rgba(34, 36, 38, 0.15);
    z-index: 1000;
  }

  .react-select__menu-list {
    padding: 0;
  }

  /* Options */
  .react-select__option {
    cursor: pointer;
    padding: 0.78571429rem 1.14285714rem;
    color: rgba(0, 0, 0, 0.87);
  }

  .react-select__option--is-focused {
    background-color: rgba(var(--primary-rgb), 0.1);
  }

  .react-select__option--is-selected {
    background-color: rgba(var(--primary-rgb), 0.2);
    color: rgba(0, 0, 0, 0.95);
  }

  .react-select__option:active {
    background-color: rgba(0, 0, 0, 0.05);
  }

  /* Secondary line under an option that carries a description — Semantic's
     Dropdown rendered these as a muted second line in the open menu. */
  .react-select__option-description {
    color: rgba(0, 0, 0, 0.5);
    display: block;
    font-size: 0.85em;
  }

  /* Option-level className passed by the caller. 'muted' is the one in use
     (the accessorial stop select's 'N/A (Not Applicable)' row), which Semantic
     rendered italic. */
  .muted {
    font-style: italic;
  }

  /* Indicators */
  .react-select__indicator-separator {
    display: none;
  }

  .react-select__dropdown-indicator {
    color: rgba(0, 0, 0, 0.87);
    padding: 8px;
    transition: transform 0.2s;

    &:hover {
      color: rgba(0, 0, 0, 0.87);
    }
  }

  .react-select__control--menu-is-open .react-select__dropdown-indicator {
    transform: rotate(180deg);
  }

  .react-select__clear-indicator {
    color: rgba(0, 0, 0, 0.87);
    padding: 8px;

    &:hover {
      color: rgba(0, 0, 0, 0.87);
    }
  }

  .react-select__loading-indicator {
    color: rgba(0, 0, 0, 0.87);
  }
`
